import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import CommonSelect from "../../../core/common/commonSelect";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import {
  selectCompaniesLoading,
  selectCompanyOptions,
} from "../../../core/data/redux/leads-queue/selectors";
import { fetchCompanies } from "../../../core/data/redux/leads-queue/thunks";
import { setActiveCompany } from "../../../core/data/redux/projects/slice";
import { selectActiveCompany } from "../../../core/data/redux/projects/selectors";
import { all_routes } from "../../router/all_routes";

import type { AppDispatch } from "../../../core/data/redux/store";

interface ProjectsHeaderProps {
  title: string;
  breadcrumb: string;
  children?: React.ReactNode;
}

const ProjectsHeader: React.FC<ProjectsHeaderProps> = ({
  title,
  breadcrumb,
  children,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const activeCompany = useSelector(selectActiveCompany);
  const companyOptions = useSelector(selectCompanyOptions);
  const companiesLoading = useSelector(selectCompaniesLoading);

  useEffect(() => {
    if (companyOptions.length === 0) {
      dispatch(fetchCompanies());
    } else if (!activeCompany?.company_id && companyOptions.length > 0) {
      const first = companyOptions[0];
      const [companyId, companyType] = first.value.split("-");
      const companyName = first.label.split("-")[0].trim();
      dispatch(
        setActiveCompany({
          company_id: companyId.trim(),
          company_name: companyName,
          company_type: companyType.trim(),
        })
      );
    }
  }, [dispatch, companyOptions, activeCompany?.company_id]);

  const handleCompanyChange = (selectedOption: any) => {
    if (!selectedOption) return;
    const [companyId, companyType] = selectedOption.value.split("-");
    const companyName = selectedOption.label.split("-")[0].trim();
    if (
      activeCompany?.company_id == companyId?.trim() &&
      activeCompany?.company_type == companyType?.trim()
    )
      return;
    dispatch(
      setActiveCompany({
        company_id: companyId.trim(),
        company_name: companyName,
        company_type: companyType.trim(),
      })
    );
  };

  return (
    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
      <div className="my-auto mb-2">
        <h2 className="mb-1">{title}</h2>
        <nav>
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to={all_routes.adminDashboard}>
                <i className="ti ti-smart-home" />
              </Link>
            </li>
            <li className="breadcrumb-item">Projects</li>
            <li className="breadcrumb-item active" aria-current="page">
              {breadcrumb}
            </li>
          </ol>
        </nav>
      </div>

      <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
        <div className="mb-2" title="Choose Company">
          <CommonSelect
            className="select"
            options={companyOptions}
            defaultValue={
              activeCompany.company_id
                ? {
                  value: `${activeCompany.company_id}-${activeCompany.company_type}`,
                  label: `${activeCompany.company_name} - ${activeCompany.company_type}`,
                }
                : undefined
            }
            onChange={handleCompanyChange}
            isDisabled={companiesLoading}
            placeHolder="Select Company"
          />
        </div>

        {children}

        <div className="head-icons ms-2">
          <CollapseHeader />
        </div>
      </div>
    </div>
  );
};

export default ProjectsHeader;
